
import React, { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Icon from '@ant-design/icons';
import classNames from 'classnames';
import VariableWidgetProcessor, {
	VariableWidgetState,
	getDefaultState
} from '@kemu-io/kemu-core/widgets/variable/index.js';
import variablesManager, { AccessibleVariable } from '@kemu-io/kemu-core/common/managers/variablesManager.js';
import kemuCore from '@kemu-io/kemu-core';
import { WidgetPortContext , VariableDefinition, WidgetType } from '@kemu-io/kemu-core/types';
import { Menu, Dropdown } from 'antd';
import { DataType } from '@kemu-io/hs-types';
import { DEFAULT_THING_ID } from '@kemu-io/kemu-core/common/constants';
import { GetPortsInformationFunction, GateUI, GateUIProps, GateCustomSettingsProps } from '../index';
import GateIcon from '../../gateIcon/gateIcon';
import { PortLocation } from '../../../types/canvas_t';
import { buildOutputPortsDescription } from '../common';
import styles from './variable.module.css';
import EditableTextbox from '@components/EditableTextbox/EditableTextbox';
import { ReactComponent as VariableWidgetIcon } from '@src/assets/img/gates/variable-widget.svg';
import useReactiveWidgetState, { NewStateOrFunction } from '@hooks/useReactiveWidgetState';
import useTranslation from '@hooks/useTranslation';
import { dataTypeToHumanReadable } from '@common/utils';
import { SETTINGS_CONTAINER_CLASS } from '@common/constants';
import KemuSwitch from '@components/form-control/kemuSwitch/KemuSwitch';
import { ReactComponent as JsonWidgetIcon } from '@src/assets/img/dataTypes/jsonIcon.svg';
import { ReactComponent as NumberIcon } from '@src/assets/img/dataTypes/numberIcon.svg';
import { ReactComponent as StringIcon } from '@src/assets/img/dataTypes/stringIcon.svg';
import { ReactComponent as ArrayIcon } from '@src/assets/img/dataTypes/arrayIcon.svg';
import { ReactComponent as ImageIcon } from '@src/assets/img/dataTypes/imageIcon.svg';
import { ReactComponent as BooleanIcon } from '@src/assets/img/dataTypes/booleanIcon.svg';
import { ReactComponent as AnythingIcon } from '@src/assets/img/dataTypes/anythingIcon.svg';
import { ReactComponent as RectIcon } from '@src/assets/img/dataTypes/rectIcon.svg';
import { ReactComponent as ArrayBufferIcon } from '@src/assets/img/dataTypes/arrayBufferIcon.svg';
import { ReactComponent as AudioBufferIcon } from '@src/assets/img/dataTypes/audioBufferIcon.svg';
import { ReactComponent as BinaryFileIcon } from '@src/assets/img/dataTypes/binaryFileIcon.svg';
import StyledButton from '@components/form-control/styledButton/styledButton';

const createVariableDefinition = async (config: {
	recipePoolId: string,
	setStateFn: (newState: NewStateOrFunction<VariableWidgetState>, triggerEvents?: boolean | undefined) => Promise<void>,
	widgetId: string,
	name: string,
	definition?: Partial<VariableDefinition & {
		reactive?: boolean;
		defaultValue?: number | string | boolean;
	}>,
}) => {
	const { setStateFn, widgetId, name, definition, recipePoolId } = config;

	// IMPORTANT: we need to pass the current state to the setStateFn to ensure
	await setStateFn(currentState => ({
		...currentState,
		reactive: definition?.reactive ?? currentState.reactive,
		type: definition?.type ?? currentState.type,
		defaultValue: definition?.defaultValue ?? currentState.defaultValue ?? undefined,
		name,
	}));

	// re-initialize the widget to create new subscriptions
	await kemuCore.initializeWidget({
		recipePoolId,
		thingRecipeId: DEFAULT_THING_ID,
		widgetRecipeId: widgetId,
	});
};


const VariableWidget = (props: GateUIProps): React.JSX.Element => {
	const { recipeId, thingRecipeId, info } = props;
	const { id: widgetId } = info;
	const [state, setWidgetState] = useReactiveWidgetState<VariableWidgetState>(recipeId, thingRecipeId, info.id);
	const [dropdownVisible, setDropdownVisible] = useState(false);
	const dropdownCntRef = useRef<HTMLDivElement>(null);
	const t = useTranslation();

	const noNameSet = !!state.name;
	const selectedVarName = state.name;

	const getVariableNames = useCallback(() => {
		const currentVars = variablesManager.getVariableNames();
		const uniqueNames: Record<string, AccessibleVariable> = {};
		for (const variable of currentVars) {
			if (!uniqueNames[variable.name]) {
				// Ignore widget groups and ui variables
				// if (!variable.isCustomFieldVar && variable.subscriberWidgetType !== WidgetType.widgetGroup) {
				if (/* !variable.isUIElement &&  */variable.subscriberWidgetType !== WidgetType.widgetGroup) {
					uniqueNames[variable.name] = variable;
				}
			}
		}

		return Object.values(uniqueNames);
	}, []);

	const [variableNames, setVariableNames] = useState<AccessibleVariable[]>(getVariableNames());

	const reloadVarNames = useCallback(() => {
		setVariableNames(getVariableNames());
	}, [getVariableNames]);


	const selectedVarOwner = variableNames.find(variable => variable.name === selectedVarName)?.subscriberWidgetId;
	const widgetIsVarOwner = selectedVarOwner === widgetId;

	const hideMenu = useCallback(() => {
		setDropdownVisible(false);
	}, []);

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const handleMenuClick = useCallback((info: any) => {
		createVariableDefinition({
			recipePoolId: recipeId,
			setStateFn: setWidgetState,
			widgetId,
			name: info.key as string,
		}).finally(() => {
			hideMenu();
		});
	}, [setWidgetState, hideMenu, widgetId, recipeId]);

	const menu = (
		<Menu className={styles.DropdownMenu} onClick={handleMenuClick} selectedKeys={[state.name || '']}>
			{variableNames.map((variable) => (
				<Menu.Item key={`${variable.name}`}>
					{variable.name}
					<span className={styles.VarType}>{dataTypeToHumanReadable(t, variable.type)}</span>
				</Menu.Item>
			))}
		</Menu>
	);

	const onNewInputEntered = useCallback((name: string) => {
		if (!name.trim()) { return; }
		createVariableDefinition({
			recipePoolId: recipeId,
			setStateFn: setWidgetState,
			widgetId,
			name,
		}).finally(() => {
			setDropdownVisible(false);
		});
	}, [setWidgetState, widgetId, recipeId]);

	const dropdownVisibilityChanged = useCallback((visible: boolean) => {
		setDropdownVisible(visible);
		if (visible) {
			reloadVarNames();
		}
	}, [reloadVarNames]);

	const getDropdownContainerEl = useCallback(() => {
		return dropdownCntRef.current as HTMLElement;
	}, []);

	useEffect(() => {
		if (!noNameSet) {
			const defaultState = getDefaultState();
			createVariableDefinition({
				recipePoolId: recipeId,
				setStateFn: setWidgetState,
				widgetId,
				name: defaultState.name,
				definition: {
					// createdByWidgetId: widgetId,
					// storeValueInRecipe: false,
					reactive: defaultState.reactive,
					type: defaultState.type,
					value: defaultState.defaultValue,
				},
			});
		}
	}, [noNameSet, setWidgetState, widgetId, recipeId]);

	// Force dropdown to close when the widget is moved
	useEffect(() => {
		setDropdownVisible(false);
	}, [info]);

	return (
		<div
			ref={dropdownCntRef}
			className={classNames(styles.GateBody, {
				[styles.WidgetDisabled]: props.info.disabled,
			})}
		>
			{/* <Icon component={VariableWidgetIcon}/> */}
			<Dropdown
				getPopupContainer={getDropdownContainerEl}
				overlay={menu}
				open={dropdownVisible}
				className="gate-dropdown"
			>
				<EditableTextbox
					placeholder={t('PressEnter', 'Press ENTER')}
					onEnterKey={onNewInputEntered}
					defaultText={state.name}
					onFocusChanged={dropdownVisibilityChanged}
					dropdownIconClassName={styles.DropArrow}
					inputClassName={classNames(styles.InputElement, {
						[styles.VarOwner]: widgetIsVarOwner,
					})}
					editing={dropdownVisible}
				/>
			</Dropdown>
		</div>
	);
};

const GateCustomSettings = (props: GateCustomSettingsProps): React.JSX.Element => {
	const { recipeId, blockId: thingRecipeId, gateInfo, onClose } = props;
	const widgetId = gateInfo.id;
	const [state, setWidgetState] = useReactiveWidgetState<VariableWidgetState>(recipeId, thingRecipeId, widgetId);
	const availableVariables = variablesManager.getVariableNames();

	const fixedState = {
		...getDefaultState(),
		...state
	};

	const [tmpState, setTmpState] = useState<VariableWidgetState>(fixedState);
	const stateChanged = JSON.stringify(tmpState) !== JSON.stringify(fixedState);
	const currentVariableName = tmpState.name;
	const rawT = useTranslation();
	const dt = useTranslation('Kemu.DataType.HumanReadable');
	const t = useTranslation('LogicMaker.Widgets.Variable.Settings');
	// const gT = useTranslation('LogicMaker.Widgets.Generic');

	const ownSelectedVar = availableVariables.find(variable =>
		variable.name === currentVariableName
		&& variable.subscriberWidgetId === widgetId
	);

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const onUpdateType = useCallback(({ key }: any) => {
		setTmpState((current) => ({
			...current,
			type: Number(key),
		}));
	}, []);

	const typesMenu = useMemo(() => (
		<Menu className={styles.TypesMenu} onClick={onUpdateType}>
			<Menu.Item key={DataType.Number}>
				<span><Icon component={NumberIcon} /></span> {dt('Number')}
			</Menu.Item>
			<Menu.Item  key={DataType.String}>
				<span><Icon component={StringIcon} /></span> {dt('String')}
			</Menu.Item>
			<Menu.Item  key={DataType.Boolean}>
				<span><Icon component={BooleanIcon} /></span> {dt('Boolean')}
			</Menu.Item>
			<Menu.Item  key={DataType.Array}>
				<span><Icon component={ArrayIcon} /></span> {dt('Array')}
			</Menu.Item>
			<Menu.Item  key={DataType.JsonObj}>
				<span><Icon component={JsonWidgetIcon} /></span> {dt('JsonObj')}
			</Menu.Item>
			<Menu.Item  key={DataType.Rect}>
				<span><Icon component={RectIcon} /></span> {dt('Rect')}
			</Menu.Item>
			<Menu.Item  key={DataType.ImageData}>
				<span><Icon component={ImageIcon} /></span> {dt('ImageData')}
			</Menu.Item>
			<Menu.Item  key={DataType.BinaryFile}>
				<span><Icon component={BinaryFileIcon} /></span> {dt('BinaryFile')}
			</Menu.Item>
			<Menu.Item  key={DataType.ArrayBuffer}>
				<span><Icon component={ArrayBufferIcon} /></span> {dt('ArrayBuffer')}
			</Menu.Item>
			<Menu.Item  key={DataType.AudioBuffer}>
				<span><Icon component={AudioBufferIcon} /></span> {dt('AudioBuffer')}
			</Menu.Item>
			<Menu.Item  key={DataType.Anything}>
				<span><Icon component={AnythingIcon} /></span> {dt('Anything')}
			</Menu.Item>
		</Menu>
	), [dt, onUpdateType]);


	const handleDefaultValueChange = (event: ChangeEvent<HTMLInputElement>) => {
		setTmpState((current) => ({
			...current,
			defaultValue: event.target.value,
		}));
	};

	const handleSaveSettings = useCallback(() => {
		createVariableDefinition({
			recipePoolId: recipeId,
			setStateFn: setWidgetState,
			widgetId,
			name: currentVariableName,
			definition: tmpState,
		});

		onClose();
	}, [onClose, setWidgetState, widgetId, currentVariableName, tmpState, recipeId]);

	// TODO: Register the reactToVariable map in the widget itself
	const handleSwitchChange = (checked: boolean) => {
		setTmpState((current) => ({
			...current,
			reactive: checked,
		}));
	};

	return (
		<div className={classNames(styles.SettingsContainer, SETTINGS_CONTAINER_CLASS)}>
			{props.children}

			{ownSelectedVar && (
				<>
					<div className={styles.Row}>
						<div className={styles.Label}>Type</div>
						<div className={styles.Element}>
							<span className={styles.TypeName}>{dataTypeToHumanReadable(rawT, tmpState.type)}</span>
							<Dropdown overlay={typesMenu} placement="bottomLeft" trigger={['click']} >
								<span className={classNames(styles.ChooseType, `type-${tmpState.type}`)}>
									{tmpState.type === DataType.Number && (
										<Icon component={NumberIcon}/>
									)}

									{tmpState.type === DataType.String && (
										<Icon component={StringIcon} />
									)}

									{tmpState.type === DataType.Array && (
										<Icon component={ArrayIcon} />
									)}

									{tmpState.type === DataType.JsonObj && (
										<Icon component={JsonWidgetIcon} />
									)}

									{tmpState.type === DataType.ImageData && (
										<Icon component={ImageIcon} />
									)}

									{tmpState.type === DataType.Boolean && (
										<Icon component={BooleanIcon} />
									)}

									{tmpState.type === DataType.Rect && (
										<Icon component={RectIcon} />
									)}

									{tmpState.type === DataType.BinaryFile && (
										<Icon component={BinaryFileIcon} />
									)}

									{tmpState.type === DataType.ArrayBuffer && (
										<Icon component={ArrayBufferIcon} />
									)}

									{tmpState.type === DataType.AudioBuffer && (
										<Icon component={AudioBufferIcon} />
									)}

									{tmpState.type === DataType.Anything && (
										<Icon component={AnythingIcon} />
									)}

								</span>
							</Dropdown>
						</div>
					</div>

					{(tmpState.type === DataType.String || tmpState.type === DataType.Number) && (
						<div className={styles.Row}>
							<div className={styles.Label}>{t('DefaultValue', 'Default value')}</div>
							<div className={styles.Element}>
								<input
									className={styles.DefaultValue}
									type={tmpState.type === DataType.String ? 'text' : 'number'}
									value={typeof tmpState.defaultValue === 'boolean' ? tmpState.defaultValue.toString() : tmpState.defaultValue ?? ''}
									onChange={handleDefaultValueChange}
								/>
							</div>
						</div>
					)}
				</>
			)}


			<div className={styles.Row}>
				<div className={styles.Label}>{t('Switch', 'React to value changes')}</div>
				<div className={styles.Element}>
					<KemuSwitch
						size="small"
						checked={tmpState.reactive}
						onChange={handleSwitchChange}
					/>
				</div>
			</div>

			<div className={styles.Row}>
				<div className={styles.SaveBtn}>
					<StyledButton
						title={t('Save', 'Save')}
						onClick={handleSaveSettings}
						noShadow={true}
						disabled={!stateChanged}
					/>
				</div>
			</div>
		</div>
	);
};


/** Icon to be added to the bar */
const GateBarIcon = (): React.JSX.Element => {
	return (
		<GateIcon icon={<Icon component={VariableWidgetIcon} />}/>
	);
};

const getPortsInformation: GetPortsInformationFunction = (state, widgetInfo) => {
	const portContext: WidgetPortContext = {
		recipePoolId: widgetInfo.recipePoolId,
		recipeType: widgetInfo.recipeType,
		thingRecipeId: widgetInfo.thingRecipeId,
		widgetId: widgetInfo.id,
	};

	const outputNames = VariableWidgetProcessor.getOutputNames(state, portContext);
	const inputNames = VariableWidgetProcessor.getInputNames(state, portContext);

	const outputs = buildOutputPortsDescription(outputNames, 0.14);
	const positions: Record<string, PortLocation> = {
		'set': [0, 0.3, -1, 0],
		'read': [0, 0.7, -1, 0],
	};

	return {
		inputs: inputNames.map(input => ({
			name: input.name,
			type: input.type,
			position: positions[input.name]
		})),

		outputs,
	};
};


export default {
	getPortsInformation,
	BarIcon: GateBarIcon,
	Element: VariableWidget,
	CustomSettingsDialog: GateCustomSettings,
	hasTitle: true,
	getWidgetTitle: (intl) => intl.formatMessage({ defaultMessage: 'Variable', id: 'LogicMapper.Widgets.Variable.Title' }),
} as GateUI;
